var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@apollo/client';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { Box, DialogContent, DialogFooter, DialogHeader, useToast } from '@pocketlaw/tetris';
import { Falcon } from '@pocketlaw/tetris/macro';
import { useCreateFileSystemAcls } from 'app/domains/filesystem';
import { CancelButton, FormDialog, SubmitButton } from 'shared/domains/forms';
import { FileSystemAccessManagementDocument } from './FileSystemAccessManagement.gql';
import { useFileSystemAccessDrawer } from './useFileSystemAccessDrawer';
import { AccessManagementList } from '../AccessManagement/AccessManagementList';
import { AccessManagementOwners } from '../AccessManagement/AccessManagementOwners';
import { AccessManagementSearch } from '../AccessManagement/AccessManagementSearch';
import { accessManagementSchema } from '../AccessManagement/accessManagementSchema';
import { calculateACLChanges } from '../AccessManagement/calculateACLChanges';
export function FileSystemAccessManagement() {
    var _a, _b;
    const { open, entryId, setEntryId } = useFileSystemAccessDrawer();
    const { addToast } = useToast();
    const { data, error, loading, refetch } = useQuery(FileSystemAccessManagementDocument, {
        fetchPolicy: 'network-only',
        skip: !(entryId === null || entryId === void 0 ? void 0 : entryId.length),
        variables: {
            entryId: entryId || '',
        },
    });
    const { createFileSystemAcls } = useCreateFileSystemAcls({ entryId });
    const initialValues = {
        userAcls: ((_a = data === null || data === void 0 ? void 0 : data.fileSystemEntry) === null || _a === void 0 ? void 0 : _a.userAcls) || [],
        teamAcls: ((_b = data === null || data === void 0 ? void 0 : data.fileSystemEntry) === null || _b === void 0 ? void 0 : _b.teamAcls) || [],
    };
    const handleOnSave = (values, helpers) => __awaiter(this, void 0, void 0, function* () {
        var _c, _d;
        try {
            yield createFileSystemAcls({
                userAcls: calculateACLChanges(initialValues.userAcls, values.userAcls),
                teamAcls: calculateACLChanges(initialValues.teamAcls, values.teamAcls),
            });
            addToast({
                title: t({
                    message: 'Access successfully given',
                    comment: 'add access to folder or document toast success message',
                }),
                appearance: 'success',
            });
            const { data: newData } = yield refetch();
            helpers.resetForm({
                values: {
                    userAcls: ((_c = newData.fileSystemEntry) === null || _c === void 0 ? void 0 : _c.userAcls) || [],
                    teamAcls: ((_d = newData.fileSystemEntry) === null || _d === void 0 ? void 0 : _d.teamAcls) || [],
                },
            });
            setEntryId(undefined);
        }
        catch (e) {
            addToast({
                title: t({
                    message: 'Something went wrong, try again.',
                    comment: 'add access to folder or document toast error message',
                }),
                appearance: 'danger',
            });
        }
    });
    if (!open) {
        return null;
    }
    return (<FormDialog initialValues={initialValues} onSubmit={handleOnSave} onClose={() => setEntryId(undefined)} validationSchema={accessManagementSchema} state={{ loading, error: Boolean(error) }} onRefetch={refetch}>
      <DialogHeader icon={<Falcon icon="lock"/>} title={t({
            message: 'Access management',
            comment: 'Filesystem access drawer title',
        })} subtitle={t({
            message: 'Manage what users and teams can see and do',
            comment: 'Filesystem access drawer subtitle',
        })}/>

      <DialogContent>
        <Box gap="md" flexDirection="column">
          <AccessManagementSearch from={data || {}} fromParentEntry={(data === null || data === void 0 ? void 0 : data.fileSystemEntry) || {}}/>
          <AccessManagementOwners from={data || {}}/>
          <AccessManagementList entryId={entryId}/>
        </Box>
      </DialogContent>

      <DialogFooter>
        <CancelButton size="small" onClick={() => setEntryId(undefined)}>
          <Trans comment="Filesystem access management dialog">Cancel</Trans>
        </CancelButton>
        <SubmitButton size="small">
          <Trans comment="Filesystem access management dialog">Save</Trans>
        </SubmitButton>
      </DialogFooter>
    </FormDialog>);
}
